import { useEffect, useState } from "react";
import {
  Activity, CheckCircle2, XCircle, Loader2, Bell, Clock, TrendingUp,
  AlertTriangle, Zap, FlaskConical,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1/flows`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

function Stat({ label, value, sub, icon: Icon, tone = "slate" }) {
  const toneMap = {
    slate: "text-slate-900 dark:text-white",
    green: "text-green-600",
    red: "text-red-600",
    amber: "text-amber-600",
    blue: "text-sky-600",
    violet: "text-violet-600",
  };
  return (
    <Card className="p-5 dark:bg-slate-800" data-testid={`insight-stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500 font-medium">{label}</div>
          <div className={`mt-2 text-3xl font-bold ${toneMap[tone]}`}>{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        {Icon && <Icon className={toneMap[tone]} size={22} />}
      </div>
    </Card>
  );
}

function EmitDebugButton() {
  const [open, setOpen] = useState(false);
  const [event, setEvent] = useState("");
  const [payload, setPayload] = useState('{\n  "amount": 50000,\n  "dealer_name": "Test Dealer"\n}');
  const [busy, setBusy] = useState(false);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/catalogue`, { headers: auth() })
      .then((r) => r.json())
      .then((j) => {
        // flatten sub-categories
        const all = [];
        (j.data || []).forEach((cat) => {
          (cat.sub_categories || []).forEach((sub) => {
            (sub.items || []).forEach((it) => all.push(it));
          });
        });
        setEvents(all);
        if (all.length && !event) setEvent(all[0].event);
      })
      .catch(() => {});
  }, [open]);

  const fire = async () => {
    if (!event) return toast.error("Pick an event");
    let parsed = {};
    try { parsed = payload.trim() ? JSON.parse(payload) : {}; }
    catch (e) { return toast.error("Payload must be valid JSON"); }
    setBusy(true);
    try {
      const r = await fetch(`${API}/_emit/${encodeURIComponent(event)}`, {
        method: "POST",
        headers: { ...auth(), "Content-Type": "application/json" },
        body: JSON.stringify({ data: parsed }),
      });
      const j = await r.json();
      if (j.success !== false) {
        toast.success(`Emitted ${event} — flows queued`);
        setOpen(false);
      } else {
        toast.error(j.error?.message || "Emit failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="emit-debug-btn">
        <FlaskConical size={14} className="mr-1" /> Emit test event
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="emit-dialog">
          <DialogHeader>
            <DialogTitle>Fire a trigger event</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Event</label>
              <Select value={event} onValueChange={setEvent}>
                <SelectTrigger data-testid="emit-event-select"><SelectValue placeholder="Pick a trigger event" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {events.map((e) => (
                    <SelectItem key={e.event} value={e.event}>
                      {e.title || e.event} <span className="text-slate-400 ml-1">· {e.event}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Payload (JSON)</label>
              <Textarea value={payload} onChange={(e) => setPayload(e.target.value)} rows={8} className="font-mono text-xs" data-testid="emit-payload-input" />
              <div className="text-[11px] text-slate-500 mt-1">
                Hits <code>POST /api/v1/flows/_emit/&lt;event&gt;</code>. Useful for verifying flows from your own integrations.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="emit-cancel">Cancel</Button>
            <Button onClick={fire} disabled={busy} className="bg-[#8B1A1A] hover:bg-[#6f1414] text-white" data-testid="emit-fire-btn">
              {busy ? <><Loader2 size={14} className="animate-spin mr-1" /> Firing</> : <><Zap size={14} className="mr-1" /> Fire event</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function FlowInsightsPage() {
  const [window, setWindow] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/insights?days=${window}`, { headers: auth() })
      .then((r) => r.json())
      .then((j) => setData(j.data))
      .finally(() => setLoading(false));
  }, [window]);

  const fmtMs = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${ms}ms`);
  const successRate = data && data.totals.runs > 0
    ? ((data.totals.completed / data.totals.runs) * 100).toFixed(1)
    : "—";

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="flow-insights-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Activity className="text-[#8B1A1A]" size={28} /> Flow Insights
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            One-glance view of how your automations are performing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <EmitDebugButton />
          <div className="w-44">
            <Select value={String(window)} onValueChange={(v) => setWindow(Number(v))}>
              <SelectTrigger data-testid="insight-window-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 24 hours</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {loading || !data ? (
        <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="animate-spin mr-2" /> Loading…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Stat label="Flows run" value={data.totals.runs} sub={`${data.active_flows} of ${data.total_flows} active`} icon={Activity} tone="slate" />
            <Stat label="Success rate" value={`${successRate}%`} sub={`${data.totals.completed} completed`} icon={CheckCircle2} tone="green" />
            <Stat label="Failed" value={data.totals.failed} sub={data.totals.running ? `${data.totals.running} still running` : "All quiet"} icon={XCircle} tone={data.totals.failed ? "red" : "slate"} />
            <Stat label="Notifications fired" value={data.notifications_dispatched} sub="Push + In-App combined" icon={Bell} tone="violet" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6" data-testid="insight-stat-avg-run">
            <Stat label="Average run time" value={fmtMs(data.avg_run_ms || 0)} sub="Includes WAIT-node sleeps" icon={Clock} tone="blue" />
            <Stat label="Window" value={`${data.window_days} day${data.window_days > 1 ? "s" : ""}`} sub={`${data.trend.length} day(s) with activity`} icon={TrendingUp} tone="amber" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <Card className="p-5 dark:bg-slate-800">
              <div className="flex items-center gap-2 mb-3">
                <Zap className="text-amber-500" size={18} />
                <h3 className="font-semibold text-slate-900 dark:text-white">Top triggers</h3>
              </div>
              {data.top_triggers.length === 0 ? (
                <div className="text-sm text-slate-500">No runs yet in this window.</div>
              ) : (
                <ol className="space-y-2" data-testid="insight-top-triggers">
                  {data.top_triggers.map((t, i) => (
                    <li key={t.event} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className="w-6 h-6 inline-flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 text-xs font-bold">{i + 1}</span>
                        <code className="text-slate-700 dark:text-slate-200">{t.event}</code>
                      </span>
                      <Badge variant="outline">{t.count} runs</Badge>
                    </li>
                  ))}
                </ol>
              )}
            </Card>

            <Card className="p-5 dark:bg-slate-800" data-testid="insight-failed-runs">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="text-red-500" size={18} />
                <h3 className="font-semibold text-slate-900 dark:text-white">Failed runs needing attention</h3>
              </div>
              {data.failed_runs.length === 0 ? (
                <div className="text-sm text-slate-500" data-testid="no-failed-runs">No failed runs in this window. 🎉</div>
              ) : (
                <div className="space-y-2">
                  {data.failed_runs.map((r) => (
                    <div key={r.run_id} className="text-xs border rounded p-2 bg-red-50 dark:bg-red-900/20">
                      <div className="font-mono">{r.run_id}</div>
                      <div className="text-slate-600 dark:text-slate-400">{r.trigger_event} · {r.error || "unknown"}</div>
                      <div className="text-slate-400 text-[10px]">{new Date(r.started_at).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {data.trend.length > 0 && (
            <Card className="p-5 dark:bg-slate-800">
              <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Daily trend</h3>
              <div className="flex items-end gap-2 h-32" data-testid="insight-trend">
                {data.trend.map((d) => {
                  const max = Math.max(...data.trend.map((x) => x.count), 1);
                  const h = (d.count / max) * 100;
                  return (
                    <div key={d.date} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                      <div className="text-xs text-slate-600 dark:text-slate-300 font-medium">{d.count}</div>
                      <div className="w-full bg-[#8B1A1A] rounded-t" style={{ height: `${h}%`, minHeight: "4px" }} />
                      <div className="text-[10px] text-slate-400 truncate max-w-full">{d.date.slice(5)}</div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
